import React, { useMemo, useState, useEffect } from "react";
import useGameBase from "../../hooks/useGameBase";

function RepeatAfterMeGame({ gameState, gamePlayers, user, onGameAction, isSpectator, onPlayAgain, socket, roomId }) {
  const { submitAction, findPlayerById, normalizedState } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const state = useMemo(() => normalizedState || gameState || {}, [normalizedState, gameState]);
  const me = (() => {
    if (!user) return null;
    return (state.players || []).find(p => p.userId === user.userId || p.socketId === user.socketId) || null;
  })();

  const [lastPressed, setLastPressed] = useState(null);
  const [now, setNow] = useState(Date.now());

  const options = useMemo(() => {
    return state.options || [];
  }, [state.options]);

  // Map playerId -> { status: 'correct'|'wrong'|null, timeTakenMs: number|null }
  const playerInfo = useMemo(() => {
    const map = {};
    try {
      if (!state || !state.players) return map;
      const phaseActive = state.phase === 'showing' || state.phase === 'collecting';
      // prefer live presses when active, otherwise use lastRoundTimings if present
      if (phaseActive) {
        const correct = state.currentLetter;
        const presses = Array.isArray(state.presses) ? state.presses : [];
        const earliest = {};
        for (const p of presses) {
          const id = p.playerId;
          if (!earliest[id] || (p.at && p.at < earliest[id].at)) earliest[id] = p;
        }
        const start = state.roundStartAt || null;
        for (const pl of state.players) {
          const id = pl.socketId || pl.userId || pl.id;
          const press = earliest[id];
          const timeTakenMs = press && start ? Math.max(0, (press.at - start)) : (press ? 0 : null);
          const status = press ? ((correct && (press.letter || "").toUpperCase() === (correct || "").toUpperCase()) ? 'correct' : 'wrong') : null;
          map[id] = { status, timeTakenMs };
        }
      } else {
        // use lastRoundTimings reported by server (if available)
        const last = state.lastRoundTimings || {};
        for (const pl of state.players) {
          const id = pl.socketId || pl.userId || pl.id;
          const info = last[id];
          map[id] = { status: info ? (info.correct ? 'correct' : (info.timeTakenMs !== null ? 'wrong' : null)) : null, timeTakenMs: info ? info.timeTakenMs : null };
        }
      }
    } catch (e) {
      // ignore
    }
    return map;
  }, [state]);

  // Sort players: fastest correct first, then fastest wrong, then unanswered.
  const sortedPlayers = useMemo(() => {
    const list = (state.players || []).slice();
    const getInfo = (p) => {
      const id = p.socketId || p.userId || p.id;
      return playerInfo && playerInfo[id] ? playerInfo[id] : { status: null, timeTakenMs: null };
    };
    const priority = (s) => (s === 'correct' ? 0 : s === 'wrong' ? 1 : 2);
    list.sort((a, b) => {
      const ai = getInfo(a);
      const bi = getInfo(b);
      const pa = priority(ai.status);
      const pb = priority(bi.status);
      if (pa !== pb) return pa - pb;
      const ta = (typeof ai.timeTakenMs === 'number') ? ai.timeTakenMs : Infinity;
      const tb = (typeof bi.timeTakenMs === 'number') ? bi.timeTakenMs : Infinity;
      if (ta !== tb) return ta - tb;
      // fallback stable order by username
      const an = (a.username || '').toString().toLowerCase();
      const bn = (b.username || '').toString().toLowerCase();
      return an < bn ? -1 : an > bn ? 1 : 0;
    });
    return list;
  }, [state.players, playerInfo]);

  // Reset local pressed indicator when the round/letter changes
  useEffect(() => {
    setLastPressed(null);
  }, [state.roundIndex, state.currentLetter, state.phase]);

  // tick for local countdown display while a round is active
  useEffect(() => {
    let t = null;
    if ((state.phase === 'showing') && state.roundEndAt) {
      t = setInterval(() => setNow(Date.now()), 200);
    } else {
      setNow(Date.now());
    }
    return () => { if (t) clearInterval(t); };
  }, [state.phase, state.roundEndAt]);

  const disabled = isSpectator || state.finished;

  // Determine if the current player has already pressed this round.
  const meId = me ? (me.socketId || me.userId || me.id) : null;
  const meInfo = meId && playerInfo ? playerInfo[meId] : null;
  const hasPressedThisRound = Boolean(lastPressed) || ((state.phase === 'showing' || state.phase === 'collecting') && meInfo && meInfo.status !== null);

  const handlePress = (letter) => {
    if (disabled) return;
    // submit press action
    submitAction({ type: 'press', letter }).then(() => {
      setLastPressed(letter);
    }).catch(()=>{});
  };

  const startHandler = () => {
    submitAction({ type: 'start_round' }).catch(()=>{});
  };

  return (
    <div className="p-4">
      <div className="mt-4">
        <div className="text-center text-4xl font-bold py-6 text-gray-800 dark:text-gray-200">
          {state.currentLetter ? state.currentLetter : (state.phase === 'finished' ? 'Finished' : 'Waiting...')}
        </div>
        {/* Timer display */}
        {state.phase === 'showing' && state.roundEndAt && (
          (() => {
            const remaining = Math.max(0, (state.roundEndAt || 0) - now);
            const secs = (remaining / 1000).toFixed(1);
            return (<div className="text-center text-sm mb-2 text-gray-600 dark:text-gray-400">Time remaining: {secs}s</div>);
          })()
        )}
            <div className="grid grid-cols-3 gap-2">
              {(() => {
                const count = state.optionsPerRound || 6;
                const items = Array.from({ length: count }).map((_, i) => options[i] || null);
                const correctLetter = (state.currentLetter || '').toString().toUpperCase();
                return items.map((opt, i) => {
                  const optVal = opt ? String(opt).toUpperCase() : '';
                  const pressedByMe = lastPressed && String(lastPressed).toUpperCase() === optVal;
                  const ringClass = pressedByMe ? (optVal === correctLetter ? 'ring-2 ring-green-400' : 'ring-2 ring-red-500') : '';
                  const btnDisabled = disabled || !opt || hasPressedThisRound;
                  const baseClass = opt ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-200' : 'bg-transparent dark:bg-transparent border border-dashed border-gray-200 dark:border-gray-700 text-gray-400';
                  const interactiveClass = btnDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer';
                  return (
                    <button
                      key={i}
                      className={`p-3 rounded flex items-center justify-center ${baseClass} ${interactiveClass} ${ringClass}`}
                      onClick={() => opt && !btnDisabled && handlePress(opt)}
                      disabled={btnDisabled}
                      aria-disabled={btnDisabled}
                      aria-label={opt ? `Option ${opt}` : 'Empty option'}
                    >
                      {opt || '\u00A0'}
                    </button>
                  );
                });
              })()}
            </div>
        <div className="mt-4 flex items-center gap-2">
          {!state.started && !state.finished && (
            <button className="px-3 py-1 bg-green-500 text-white rounded" onClick={startHandler} disabled={disabled}>Start</button>
          )}
          {state.finished && onPlayAgain && (
            <button className="px-3 py-1 bg-blue-500 text-white rounded" onClick={onPlayAgain}>Play Again</button>
          )}
        </div>
      </div>

      <div className="mt-6 text-sm">
        <ul className="mt-2">
          {sortedPlayers && sortedPlayers.map((p) => {
            const id = p.socketId || p.userId || p.id;
            const info = playerInfo && playerInfo[id];
            const status = info ? info.status : null;
            const timeMs = info ? info.timeTakenMs : null;
            const timeLabel = (typeof timeMs === 'number' && timeMs !== null) ? `${(timeMs/1000).toFixed(2)}s` : '';
            return (
              <li key={id} className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <span className={`${status === 'correct' ? 'text-green-600' : status === 'wrong' ? 'text-red-600' : 'text-gray-800 dark:text-gray-200'}`}>
                    {p.username || id}
                  </span>
                  {timeLabel && (
                    <span className={`text-xs ${status === 'correct' ? 'text-green-600' : status === 'wrong' ? 'text-red-600' : 'text-gray-500 dark:text-gray-400'}`}>
                      {timeLabel}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}


RepeatAfterMeGame.Guide = function RepeatAfterMeGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">Repeat After Me</h3>
      <p>The rules are simple. Pick the letter that matches what you are given the fastest to win a point.</p>
      
    </div>
  );
};


export default RepeatAfterMeGame;
